import React, { useCallback, useMemo } from "react";
import { Box, Flex, Grid, Image, Label, Link } from "theme-ui";
import P from "components/typography/P";
import PhotoUpload from "containers/Projects/components/PhotoUpload";
import { trackActivity } from "api/ActivityTracker";

export type DispatchPhoto = {
  id?: string;
  url?: string;
  createdAt?: string;
  description?: string;
  uploadedBy?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  } | null;
  purchaseOrder?: {
    id?: string;
  } | null;
};

type PhotosProps = {
  dispatch?: any;
  onPhotoUploaded?: () => Promise<void> | void;
};

const Photos: React.FC<PhotosProps> = ({ dispatch, onPhotoUploaded }) => {
  const photos: DispatchPhoto[] = useMemo(() => {
    if (!Array.isArray(dispatch?.photos)) {
      return [];
    }
    return dispatch.photos.filter((photo: DispatchPhoto) => Boolean(photo?.url));
  }, [dispatch]);

  const projectId = dispatch?.project?.id;
  const dispatchId = dispatch?.id;
  const purchaseOrderId = dispatch?.purchaseOrder?.id;

  const handleAfterUpload = useCallback(() => {
    if (typeof onPhotoUploaded === "function") {
      onPhotoUploaded();
    }
  }, [onPhotoUploaded]);

  const formatDate = (value?: string) => {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const resolveUploader = (photo: DispatchPhoto) => {
    const first = photo?.uploadedBy?.firstName;
    const last = photo?.uploadedBy?.lastName;
    const name = [first, last].filter(Boolean).join(" ").trim();

    if (name) {
      return name;
    }

    return photo?.uploadedBy?.email || "Driver";
  };

  const renderPhotos = () => {
    if (!photos.length) {
      return (
        <Flex
          sx={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            paddingTop: "40px",
            color: "#6B7280",
            fontFamily: "Rubik",
            fontSize: "14px",
            textAlign: "center",
          }}
        >
          There are no photos for this dispatch yet.
        </Flex>
      );
    }

    return (
      <Grid
        gap={"10px"}
        sx={{
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          marginTop: "20px",
        }}
      >
        {photos.map((photo) => (
          <Flex
            key={photo.id}
            sx={{
              flexDirection: "column",
              borderRadius: "8px",
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <Link
              href={photo.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "block", cursor: "pointer", color: "inherit", textDecoration: "none" }}
            >
              <Image
                src={photo.url}
                alt={photo?.description || "Dispatch photo"}
                sx={{
                  width: "100%",
                  height: "220px",
                  objectFit: "cover",
                  display: "block",
                }}
              />
            </Link>
            <Flex sx={{ flexDirection: "column", padding: "16px", gap: "6px" }}>
              <Label sx={{ fontFamily: "Poppins", fontSize: "12px", color: "#6B7280" }}>
                Uploaded by {resolveUploader(photo)} on {formatDate(photo?.createdAt)}
              </Label>
              {photo?.description ? (
                <P fontFamily="Poppins" fontSize="13px" lineHeight="20px" marginTop="6px" marginBottom="0">
                  {photo.description}
                </P>
              ) : null}
              <Link
                href={photo.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontFamily: "Rubik",
                  fontSize: "12px",
                  color: "#111827",
                  textDecoration: "underline",
                  marginTop: "auto",
                  "&:visited": {
                    color: "#111827",
                  },
                }}
                onClick={() => {
                  if (!dispatchId || !photo?.id) return;
                  void trackActivity({
                    module: "dispatches",
                    action: "view_dispatch_photo",
                    actionType: "click",
                    userId: null,
                    companyId: dispatch?.company?.id ?? dispatch?.project?.company?.id ?? null,
                    entityType: "dispatch",
                    entityId: dispatchId,
                    metadata: {
                      projectId: projectId ?? null,
                      purchaseOrderId: purchaseOrderId ?? null,
                      photoId: photo.id ?? null,
                      photoName: photo.description ?? null,
                    },
                    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
                  });
                }}
              >
                View
              </Link>
            </Flex>
          </Flex>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ width: "100%", marginTop: "20px" }}>
      <Box sx={{ marginBottom: "16px" }}>
        <PhotoUpload
          title="Upload photo"
          project={dispatch?.project}
          dispatchId={dispatchId}
          purchaseOrderId={purchaseOrderId}
          afterUpload={handleAfterUpload}
          buttonScheme="secondary"
          buttonStyles={{ width: "100%", justifyContent: "center", height: "40px", maxHeight: "40px", fontSize: "16px" }}
          disabled={!projectId || !dispatchId}
        />
      </Box>
      {renderPhotos()}
    </Box>
  );
};

export default Photos;
